/* Batch-D preview — visual mockup of the 3 features the user is evaluating
   before authorization:
     1. Help / Support button in sidebar (with popover)
     2. Cmd+K command palette (orders / invoices / suppliers / pages)
     3. KYB step-by-step flow with real file upload

   This page is fully self-contained (no API calls, no backend writes). Any
   button that "saves" simply previews the next state in-place. The actual
   wired-up production version will land in PortalShell + BuyerSettings only
   once you give me the green light.
*/
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, ArrowRight, ShoppingBag, Receipt, Building2, FileText,
  HelpCircle, BookOpen, Mail, MessageSquare, Send, X, Check,
  Upload, FileCheck2, Shield, Sparkles, Command, ChevronRight,
  AlertCircle, Loader2, ExternalLink, Hash,
} from "lucide-react";

const ACCENT = "#E8FF3A";
const INK = "#0A1F1C";

export default function BatchDPreview() {
  const [section, setSection] = useState("help"); // help | palette | kyb
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Cmd+K listener for the palette demo
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
      if (e.key === "Escape") setPaletteOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="batch-d-preview">
      {/* Top hero */}
      <header className="border-b border-border bg-white">
        <div className="max-w-6xl mx-auto px-8 py-10">
          <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55">Preview · Batch D · not wired into production yet</div>
          <h1 className="display text-4xl md:text-5xl mt-3 tracking-tight">Universal polish — feature preview</h1>
          <p className="text-sm md:text-base text-foreground/65 mt-3 max-w-3xl">
            Three small but high-leverage additions to the buyer portal. Click each one below to see how it'll look and feel.
            Nothing here is saved or sent — once you approve, I'll wire it into <code className="mono text-xs bg-secondary/60 px-1.5 py-0.5 rounded">PortalShell</code> and <code className="mono text-xs bg-secondary/60 px-1.5 py-0.5 rounded">BuyerSettings</code>.
          </p>

          {/* Section tabs */}
          <nav className="mt-8 inline-flex p-1 rounded-full border border-border bg-secondary/40" data-testid="preview-tabs">
            {[
              { k: "help",    label: "Help / Support" },
              { k: "palette", label: "Cmd+K palette" },
              { k: "kyb",     label: "KYB step-by-step" },
            ].map(t => (
              <button key={t.k} onClick={() => setSection(t.k)} data-testid={`tab-${t.k}`}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${section === t.k ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}>
                {t.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-12">
        {section === "help"    && <HelpPreview/>}
        {section === "palette" && <PalettePreview onOpen={() => setPaletteOpen(true)}/>}
        {section === "kyb"     && <KybPreview/>}
      </main>

      {/* Cmd+K palette demo overlay (shared across sections so the keyboard
          shortcut works anywhere on this preview page) */}
      {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)}/>}

      {/* Floating helper */}
      <div className="fixed bottom-8 right-8 rounded-full border border-border bg-white shadow-lg px-4 py-2.5 text-xs text-foreground/65 flex items-center gap-2" data-testid="hint-cmdk">
        <Command className="w-3.5 h-3.5"/>
        Press <kbd className="mono px-1.5 py-0.5 rounded bg-secondary text-[10px]">⌘K</kbd> anywhere on this page
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   1. HELP / SUPPORT
   Left side — sketch of the new sidebar entry; right side — popover demo.
   ───────────────────────────────────────────────────────────────────────── */
function HelpPreview() {
  const [open, setOpen] = useState(true);
  const [tab, setTab] = useState("docs"); // docs | contact | ticket
  const [ticket, setTicket] = useState({ subject: "", body: "", urgency: "normal" });
  const [sent, setSent] = useState(false);

  const submitTicket = (e) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-8" data-testid="help-preview">
      {/* Sidebar mock */}
      <aside className="rounded-2xl border border-border bg-white overflow-hidden h-fit">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-foreground grid place-items-center"><span className="text-accent text-xs font-bold">T</span></div>
          <div>
            <div className="font-semibold text-sm leading-tight">TRAQO</div>
            <div className="text-[10px] uppercase tracking-widest text-foreground/50">Buyer Portal</div>
          </div>
        </div>
        <div className="p-3 space-y-1">
          <SidebarItem label="Dashboard" muted/>
          <SidebarItem label="Orders" muted/>
          <SidebarItem label="Payments" muted/>
          <SidebarItem label="Shipping" muted/>
          <SidebarItem label="Score" muted/>
          <div className="my-3 border-t border-border"/>
          <SidebarItem label="Settings" muted/>
          {/* The new entry */}
          <button onClick={() => setOpen(v => !v)} data-testid="sidebar-help-btn"
            className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${open ? "bg-foreground text-background" : "text-foreground/70 hover:bg-secondary"}`}>
            <HelpCircle className="w-4 h-4"/>
            <span className="flex-1 text-left">Help &amp; Support</span>
            <span className="text-[10px] mono opacity-70">NEW</span>
          </button>
        </div>
      </aside>

      {/* Popover demo */}
      <div>
        <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55">When the buyer clicks "Help &amp; Support"</div>
        <h2 className="display text-2xl mt-2">A 3-tab popover opens — instantly answer 90% of questions, escalate the rest.</h2>
        <p className="text-sm text-foreground/65 mt-2 max-w-2xl">No page navigation, no lost context. Sticky on the right, dismissable with Esc.</p>

        {open && (
          <div className="mt-6 rounded-2xl border border-border bg-white shadow-sm overflow-hidden max-w-[460px]" data-testid="help-popover">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <HelpCircle className="w-4 h-4 text-foreground/70"/>
                <div className="font-medium text-sm">How can we help?</div>
              </div>
              <button onClick={() => setOpen(false)} className="text-foreground/45 hover:text-foreground"><X className="w-4 h-4"/></button>
            </div>
            <div className="px-3 pt-3">
              <div className="inline-flex rounded-full bg-secondary/50 p-0.5 text-xs">
                {[
                  { k: "docs",    l: "Docs" },
                  { k: "contact", l: "Contact" },
                  { k: "ticket",  l: "Open ticket" },
                ].map(t => (
                  <button key={t.k} onClick={() => setTab(t.k)} data-testid={`help-tab-${t.k}`}
                    className={`px-3 py-1.5 rounded-full transition-colors ${tab === t.k ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}>
                    {t.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-5 space-y-3 text-sm">
              {tab === "docs" && (
                <>
                  <Search className="w-3.5 h-3.5 text-foreground/40 absolute mt-3 ml-3"/>
                  <input placeholder="Search the docs (e.g. how to insure an invoice)" className="w-full h-10 rounded-lg border border-border bg-secondary/30 pl-9 pr-3 text-sm"/>
                  <div className="space-y-1 mt-3">
                    {[
                      "Getting started — connecting your first supplier",
                      "How TRAQO Score is calculated (5 signals)",
                      "Net-30 / Net-60 / Net-90 — paying with credit",
                      "Drop-shipping: how 3-ID reconciliation works",
                      "Shipping: live rates from Verqo Shipping",
                    ].map((d, i) => (
                      <a key={i} href="#" className="flex items-center justify-between rounded-lg px-3 py-2 hover:bg-secondary/40 text-[13px]">
                        <span><BookOpen className="w-3 h-3 inline mr-1.5 text-foreground/45"/>{d}</span>
                        <ExternalLink className="w-3 h-3 text-foreground/30"/>
                      </a>
                    ))}
                  </div>
                </>
              )}
              {tab === "contact" && (
                <div className="space-y-2.5">
                  <a href="mailto:support@traqo.com" className="flex items-center gap-3 rounded-xl border border-border p-3 hover:bg-secondary/40">
                    <div className="w-9 h-9 rounded-lg bg-secondary grid place-items-center"><Mail className="w-4 h-4"/></div>
                    <div className="flex-1">
                      <div className="font-medium text-[13px]">support@traqo.com</div>
                      <div className="text-[11px] text-foreground/55">Replies typically within 4 business hours</div>
                    </div>
                    <ArrowRight className="w-3.5 h-3.5 text-foreground/40"/>
                  </a>
                  <a href="#" className="flex items-center gap-3 rounded-xl border border-border p-3 hover:bg-secondary/40">
                    <div className="w-9 h-9 rounded-lg bg-secondary grid place-items-center"><MessageSquare className="w-4 h-4"/></div>
                    <div className="flex-1">
                      <div className="font-medium text-[13px]">Live chat</div>
                      <div className="text-[11px] text-foreground/55">Mon–Fri 9am–6pm ET</div>
                    </div>
                    <span className="chip bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">Online</span>
                  </a>
                  <a href="#" className="flex items-center gap-3 rounded-xl border border-border p-3 hover:bg-secondary/40">
                    <div className="w-9 h-9 rounded-lg bg-secondary grid place-items-center"><BookOpen className="w-4 h-4"/></div>
                    <div className="flex-1">
                      <div className="font-medium text-[13px]">Browse docs</div>
                      <div className="text-[11px] text-foreground/55">Self-serve — most common questions</div>
                    </div>
                    <ArrowRight className="w-3.5 h-3.5 text-foreground/40"/>
                  </a>
                </div>
              )}
              {tab === "ticket" && (
                sent ? (
                  <div className="text-center py-6" data-testid="help-ticket-sent">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 grid place-items-center mx-auto"><Check className="w-5 h-5 text-emerald-700"/></div>
                    <div className="font-medium mt-3">Ticket #TRQ-43902 opened</div>
                    <div className="text-xs text-foreground/55 mt-1">We'll email you at alex@apexwholesale.com.</div>
                  </div>
                ) : (
                  <form onSubmit={submitTicket} className="space-y-2.5">
                    <input value={ticket.subject} onChange={e => setTicket({...ticket, subject: e.target.value})} placeholder="What's going on?" required className="w-full h-10 rounded-lg border border-border px-3 text-sm" data-testid="help-ticket-subject"/>
                    <textarea value={ticket.body} onChange={e => setTicket({...ticket, body: e.target.value})} placeholder="Add any details (order number, error message, screenshots can be pasted)…" required rows={4} className="w-full rounded-lg border border-border px-3 py-2 text-sm"/>
                    <select value={ticket.urgency} onChange={e => setTicket({...ticket, urgency: e.target.value})} className="w-full h-10 rounded-lg border border-border px-3 text-sm">
                      <option value="low">Low — when you can</option>
                      <option value="normal">Normal — within a day</option>
                      <option value="high">High — money is stuck</option>
                      <option value="urgent">Urgent — production down</option>
                    </select>
                    <button type="submit" data-testid="help-ticket-send" className="w-full h-10 rounded-full bg-foreground text-background hover:bg-foreground/90 text-sm font-medium inline-flex items-center justify-center">
                      <Send className="w-3.5 h-3.5 mr-1.5"/>Open ticket
                    </button>
                  </form>
                )
              )}
            </div>
          </div>
        )}

        <BuildNotes items={[
          "New sidebar entry under Account section: 'Help & Support'",
          "Popover with 3 tabs: Docs (live search), Contact (email/live-chat/docs), Open ticket (subject/body/urgency)",
          "Tickets persist to a new collection: support_tickets (buyer_id, subject, body, urgency, status, messages array)",
          "Email auto-routes to support@traqo.com (Resend integration) — visible from /admin/support if you want admin oversight later",
        ]}/>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   2. Cmd+K COMMAND PALETTE
   Static visual; the actual ⌘K shortcut works via the listener above.
   ───────────────────────────────────────────────────────────────────────── */
function PalettePreview({ onOpen }) {
  return (
    <div data-testid="palette-preview">
      <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55">Cmd+K · universal portal search</div>
      <h2 className="display text-2xl mt-2">Search anything from anywhere — orders, invoices, suppliers, pages.</h2>
      <p className="text-sm text-foreground/65 mt-2 max-w-2xl">
        Press <kbd className="mono px-1.5 py-0.5 rounded bg-secondary text-[10px]">⌘K</kbd> (or <kbd className="mono px-1.5 py-0.5 rounded bg-secondary text-[10px]">Ctrl+K</kbd>) to open. Inspired by Linear, Vercel, Raycast.
        Type-ahead matches across <em>everything</em> the buyer can see — no server roundtrip per keystroke (debounced + cached).
      </p>

      <div className="mt-8 rounded-2xl border border-border bg-white p-7">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="display text-base">What it searches</h3>
            <ul className="mt-3 space-y-2 text-sm text-foreground/75">
              <li className="flex items-start gap-2"><ShoppingBag className="w-3.5 h-3.5 mt-0.5 text-foreground/45 shrink-0"/><span>Orders by code, supplier, SKU, status</span></li>
              <li className="flex items-start gap-2"><Receipt className="w-3.5 h-3.5 mt-0.5 text-foreground/45 shrink-0"/><span>Invoices by code, amount, supplier, due date</span></li>
              <li className="flex items-start gap-2"><Building2 className="w-3.5 h-3.5 mt-0.5 text-foreground/45 shrink-0"/><span>Suppliers + pending invites</span></li>
              <li className="flex items-start gap-2"><FileText className="w-3.5 h-3.5 mt-0.5 text-foreground/45 shrink-0"/><span>Pages — any portal route by name</span></li>
              <li className="flex items-start gap-2"><Hash className="w-3.5 h-3.5 mt-0.5 text-foreground/45 shrink-0"/><span>Quick actions — "New PO", "Insure invoice", "Top up wallet"</span></li>
            </ul>
          </div>
          <div>
            <h3 className="display text-base">Keyboard model</h3>
            <ul className="mt-3 space-y-2 text-sm text-foreground/75">
              <li><kbd className="mono px-1.5 py-0.5 rounded bg-secondary text-[10px]">⌘K</kbd> open palette · anywhere</li>
              <li><kbd className="mono px-1.5 py-0.5 rounded bg-secondary text-[10px]">↑ ↓</kbd> navigate</li>
              <li><kbd className="mono px-1.5 py-0.5 rounded bg-secondary text-[10px]">Enter</kbd> select / open</li>
              <li><kbd className="mono px-1.5 py-0.5 rounded bg-secondary text-[10px]">Esc</kbd> close</li>
              <li className="text-foreground/55"><kbd className="mono px-1.5 py-0.5 rounded bg-secondary text-[10px]">/</kbd> + filter prefix (e.g. <code className="mono">/inv</code>) limits to invoices</li>
            </ul>
          </div>
        </div>

        <button onClick={onOpen} data-testid="palette-open-btn"
          className="mt-7 inline-flex items-center gap-2.5 rounded-full bg-foreground text-background px-5 h-11 hover:bg-foreground/90 transition-colors text-sm">
          <Search className="w-4 h-4"/>Open command palette preview
          <span className="ml-2 inline-flex items-center gap-1 text-[10px] mono opacity-70">⌘K</span>
        </button>
      </div>

      <BuildNotes items={[
        "New global keyboard listener mounted in PortalShell (Cmd+K / Ctrl+K)",
        "Palette UI: shadcn Command primitive (already installed) — fuzzy match, grouped results",
        "Data sources: in-memory cache of /buyer/orders, /buyer/invoices, /buyer/connections refreshed every 30s",
        "Quick actions: 'New PO' → /buyer/po-generator, 'Top up wallet' → /buyer/payments#wallet",
        "Recently visited routes pinned to top (localStorage)",
      ]}/>
    </div>
  );
}

function CommandPalette({ onClose }) {
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Mock dataset (production version pulls from real APIs)
  const groups = useMemo(() => ([
    { title: "Orders", icon: ShoppingBag, items: [
      { id: "ORD-9069", title: "ORD-9069 · GlowCo · Radiance Serum 30ml", sub: "$18.50 · Awaiting confirm", to: "/buyer/orders" },
      { id: "ORD-PO-3806", title: "ORD-PO-3806 · GlowCo · Radiance Serum 30ml", sub: "$55.50 · Awaiting confirm", to: "/buyer/orders" },
      { id: "ORD-4587", title: "ORD-4587 · GlowCo · Hydra Cream Jar", sub: "$37.00 · Processing", to: "/buyer/orders" },
    ]},
    { title: "Invoices", icon: Receipt, items: [
      { id: "INV-2611", title: "INV-2611 · GlowCo Beauty", sub: "$3,024.00 · Due in 14d", to: "/buyer/payments#activity" },
      { id: "INV-2610", title: "INV-2610 · Northmill Coffee Co.", sub: "$1,824.00 · Paid", to: "/buyer/payments#activity" },
      { id: "INV-28801C", title: "INV-28801C · TEST_SUPPLIER", sub: "$1,500.00 · Repaid", to: "/buyer/hub/financing" },
    ]},
    { title: "Suppliers", icon: Building2, items: [
      { id: "glowco", title: "GlowCo Beauty", sub: "Net-60 · +$25,000 credit · 465 orders", to: "/buyer/suppliers" },
      { id: "northmill", title: "Northmill Coffee Co.", sub: "Net-30 · 12 orders", to: "/buyer/suppliers" },
    ]},
    { title: "Pages", icon: FileText, items: [
      { id: "score", title: "TRAQO Score", sub: "/buyer/score", to: "/buyer/score" },
      { id: "po-inbox", title: "Universal PO Inbox", sub: "/buyer/po-inbox", to: "/buyer/po-inbox" },
      { id: "shipping", title: "Shipping (live Verqo Shipping)", sub: "/buyer/shipping", to: "/buyer/shipping" },
      { id: "settings", title: "Settings → Business details", sub: "/buyer/settings", to: "/buyer/settings" },
    ]},
    { title: "Quick actions", icon: Sparkles, items: [
      { id: "new-po", title: "New PO", sub: "Build a Purchase Order — any supplier", to: "/buyer/po-generator", action: true },
      { id: "topup", title: "Top up wallet", sub: "Add funds via ACH / card / wire", to: "/buyer/payments#wallet", action: true },
    ]},
  ]), []);

  const filtered = useMemo(() => {
    if (!q.trim()) return groups;
    const needle = q.toLowerCase();
    return groups
      .map(g => ({ ...g, items: g.items.filter(i => (i.title + " " + (i.sub || "")).toLowerCase().includes(needle)) }))
      .filter(g => g.items.length > 0);
  }, [groups, q]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="cmdk-overlay">
      <div onClick={e => e.stopPropagation()}
        className="w-full max-w-xl mx-4 rounded-2xl bg-white border border-border shadow-2xl overflow-hidden">
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border">
          <Search className="w-4 h-4 text-foreground/45 shrink-0"/>
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search orders, invoices, suppliers, pages, actions…"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-foreground/45"
            data-testid="cmdk-input"/>
          <kbd className="mono px-1.5 py-0.5 rounded bg-secondary text-[10px]">Esc</kbd>
        </div>
        <div className="max-h-[55vh] overflow-y-auto">
          {filtered.length === 0 && (
            <div className="px-4 py-12 text-center text-sm text-foreground/55">No results for "{q}"</div>
          )}
          {filtered.map(g => (
            <div key={g.title}>
              <div className="px-4 pt-3 pb-1 text-[10px] mono uppercase tracking-[0.18em] text-foreground/50 inline-flex items-center gap-1.5">
                <g.icon className="w-3 h-3"/>{g.title}
              </div>
              {g.items.slice(0, 5).map(item => (
                <a key={item.id} href={item.to}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/40 transition-colors"
                  data-testid={`cmdk-result-${item.id}`}>
                  <div className="w-8 h-8 rounded-lg bg-secondary grid place-items-center shrink-0"><g.icon className="w-3.5 h-3.5"/></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{item.title}</div>
                    <div className="text-[11px] text-foreground/55 truncate">{item.sub}</div>
                  </div>
                  {item.action ? <span className="chip text-[9px] bg-accent/30 border border-accent/40">Action</span> : <ChevronRight className="w-3.5 h-3.5 text-foreground/30"/>}
                </a>
              ))}
            </div>
          ))}
        </div>
        <div className="border-t border-border px-4 py-2 text-[11px] text-foreground/55 flex items-center justify-between">
          <span>Type <code className="mono bg-secondary/60 px-1 rounded">/inv</code>, <code className="mono bg-secondary/60 px-1 rounded">/ord</code>, <code className="mono bg-secondary/60 px-1 rounded">/sup</code> to filter</span>
          <span className="inline-flex items-center gap-2"><kbd className="mono px-1.5 py-0.5 rounded bg-secondary text-[10px]">↵</kbd>open</span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   3. KYB STEP-BY-STEP (with real file upload)
   ───────────────────────────────────────────────────────────────────────── */
function KybPreview() {
  const [step, setStep] = useState(1);
  const [docs, setDocs] = useState([]);  // {name, size, kind, status}

  const advance = () => setStep(s => Math.min(5, s + 1));
  const back = () => setStep(s => Math.max(1, s - 1));

  const uploadStub = (kind) => {
    const name = ({
      ein:        "EIN-letter.pdf",
      reseller:   "ResellerCertificate-NY.pdf",
      bank:       "Chase-bank-letter.pdf",
      ownership:  "Articles-of-Org.pdf",
    })[kind] || "doc.pdf";
    setDocs(d => [...d, { name, size: Math.floor(Math.random() * 800 + 120), kind, status: "uploading" }]);
    setTimeout(() => {
      setDocs(d => d.map(x => x.kind === kind ? { ...x, status: "verified" } : x));
    }, 1500);
  };

  return (
    <div data-testid="kyb-preview">
      <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55">Settings → Business details · KYB step-by-step</div>
      <h2 className="display text-2xl mt-2">Replace the stale "Pending verification" badge with a real, guided flow.</h2>
      <p className="text-sm text-foreground/65 mt-2 max-w-2xl">
        Files upload to TRAQO object storage (chunked + presigned URLs) — same pattern Stripe uses for KYB.
        Each step has a clear next action; the buyer always knows what's needed.
      </p>

      <div className="mt-8 rounded-2xl border border-border bg-white p-7">
        {/* Stepper */}
        <ol className="grid grid-cols-5 gap-2 mb-7" data-testid="kyb-stepper">
          {[
            { n: 1, l: "Business basics" },
            { n: 2, l: "Tax / EIN" },
            { n: 3, l: "Ownership" },
            { n: 4, l: "Bank verification" },
            { n: 5, l: "Review & submit" },
          ].map(s => (
            <li key={s.n} className={`flex items-center gap-2 text-xs ${step >= s.n ? "" : "opacity-50"}`}>
              <div className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-medium shrink-0 ${
                step > s.n ? "bg-emerald-500 text-white" :
                step === s.n ? "bg-foreground text-background" :
                "bg-secondary text-foreground/55"}`}>
                {step > s.n ? <Check className="w-3.5 h-3.5"/> : s.n}
              </div>
              <span className="truncate">{s.l}</span>
            </li>
          ))}
        </ol>

        {/* Step content */}
        {step === 1 && <KybStepBasics/>}
        {step === 2 && <KybStepDocs kind="ein" label="EIN letter from IRS" desc="We use this to match your EIN against IRS public records." docs={docs} onUpload={() => uploadStub("ein")}/>}
        {step === 3 && <KybStepDocs kind="ownership" label="Articles of Organization / Beneficial owners" desc="List anyone who owns ≥25% of the business." docs={docs} onUpload={() => uploadStub("ownership")}/>}
        {step === 4 && <KybStepDocs kind="bank" label="Bank verification letter" desc="Or connect your bank with Plaid to skip this step." docs={docs} onUpload={() => uploadStub("bank")} extra={
          <button className="rounded-full border border-border bg-secondary/50 hover:bg-secondary px-4 h-10 text-sm inline-flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5"/>Connect with Plaid (skip upload)
          </button>
        }/>}
        {step === 5 && <KybStepReview docs={docs}/>}

        {/* Nav */}
        <div className="mt-7 pt-5 border-t border-border flex items-center justify-between">
          <button onClick={back} disabled={step === 1} className="text-sm text-foreground/65 hover:text-foreground disabled:opacity-30">← Back</button>
          {step < 5 ? (
            <button onClick={advance} data-testid={`kyb-next-${step}`} className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-10 px-5 text-sm inline-flex items-center gap-2">
              Continue<ArrowRight className="w-3.5 h-3.5"/>
            </button>
          ) : (
            <button data-testid="kyb-submit" className="rounded-full text-foreground h-10 px-5 text-sm inline-flex items-center gap-2"
              style={{ backgroundColor: ACCENT }}>
              <Shield className="w-3.5 h-3.5"/>Submit for review
            </button>
          )}
        </div>
      </div>

      <BuildNotes items={[
        "5-step wizard replaces the stale 'Pending verification' badge in Settings → Business details",
        "File upload via TRAQO object storage (presigned URL pattern, chunked >5 MB)",
        "Files persisted on user.kyb_documents[] with {kind, file_id, status, verified_at}",
        "Plaid integration short-circuits the bank step (already wired — just surfaces the link here)",
        "Submit triggers Middesk verification (already wired — currently blocked on Middesk live key)",
        "Admin sees pending KYB queue at /admin/kyb (new) with one-click approve/deny",
      ]}/>
    </div>
  );
}

function KybStepBasics() {
  return (
    <div className="space-y-4 max-w-xl" data-testid="kyb-step-1">
      <div>
        <Label>Legal business name</Label>
        <input defaultValue="Apex Wholesale LLC" className="mt-1.5 w-full h-11 rounded-md border border-input bg-white px-3 text-sm"/>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Entity type</Label>
          <select defaultValue="LLC" className="mt-1.5 w-full h-11 rounded-md border border-input bg-white px-3 text-sm">
            <option>LLC</option><option>C-Corp</option><option>S-Corp</option><option>Sole Proprietor</option><option>Partnership</option>
          </select>
        </div>
        <div><Label>State of incorporation</Label>
          <select defaultValue="NY" className="mt-1.5 w-full h-11 rounded-md border border-input bg-white px-3 text-sm">
            <option>NY</option><option>DE</option><option>CA</option><option>TX</option><option>FL</option>
          </select>
        </div>
      </div>
      <div>
        <Label>EIN (Tax ID)</Label>
        <input defaultValue="•• ••• ••42" className="mt-1.5 w-full h-11 rounded-md border border-input bg-white px-3 text-sm mono"/>
      </div>
      <div>
        <Label>Business address</Label>
        <input defaultValue="123 Industrial Way, Brooklyn, NY 11201" className="mt-1.5 w-full h-11 rounded-md border border-input bg-white px-3 text-sm"/>
      </div>
    </div>
  );
}

function KybStepDocs({ kind, label, desc, docs, onUpload, extra }) {
  const myDocs = docs.filter(d => d.kind === kind);
  return (
    <div data-testid={`kyb-step-${kind}`}>
      <div className="font-medium text-sm">{label}</div>
      <div className="text-xs text-foreground/55 mt-0.5">{desc}</div>

      <div className="mt-4 rounded-xl border-2 border-dashed border-border p-7 text-center bg-secondary/20 max-w-xl">
        <Upload className="w-7 h-7 mx-auto text-foreground/45"/>
        <div className="text-sm font-medium mt-2">Drop a PDF, PNG or JPG here</div>
        <div className="text-[11px] text-foreground/55 mt-0.5">Or click to browse · max 25 MB · scanned copies welcome</div>
        <button onClick={onUpload} data-testid={`kyb-upload-${kind}`} className="mt-4 rounded-full bg-foreground text-background hover:bg-foreground/90 h-10 px-5 text-sm inline-flex items-center gap-2">
          <Upload className="w-3.5 h-3.5"/>Browse files
        </button>
      </div>

      {extra && <div className="mt-3">{extra}</div>}

      {myDocs.length > 0 && (
        <ul className="mt-5 space-y-2 max-w-xl" data-testid={`kyb-docs-${kind}`}>
          {myDocs.map((d, i) => (
            <li key={i} className="flex items-center gap-3 rounded-xl border border-border p-3">
              <div className="w-9 h-9 rounded-lg bg-secondary grid place-items-center shrink-0">
                <FileCheck2 className="w-4 h-4"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{d.name}</div>
                <div className="text-[11px] text-foreground/55">{d.size} KB · {kind}</div>
              </div>
              {d.status === "uploading" ? (
                <span className="chip bg-secondary text-[10px] inline-flex items-center gap-1"><Loader2 className="w-2.5 h-2.5 animate-spin"/>Uploading</span>
              ) : (
                <span className="chip bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] inline-flex items-center gap-1"><Check className="w-2.5 h-2.5"/>Verified</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function KybStepReview({ docs }) {
  return (
    <div className="space-y-3 max-w-xl" data-testid="kyb-step-review">
      <div className="rounded-xl border border-border bg-secondary/30 p-5">
        <div className="text-[10px] mono uppercase tracking-widest text-foreground/55">Review</div>
        <div className="text-sm mt-1 font-medium">Apex Wholesale LLC · LLC · NY · EIN ending 42</div>
        <div className="text-xs text-foreground/60 mt-2">123 Industrial Way, Brooklyn, NY 11201</div>
      </div>
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="text-[10px] mono uppercase tracking-widest text-foreground/55 mb-2">Documents</div>
        <ul className="space-y-2 text-sm">
          {[
            { kind: "ein",       l: "EIN letter from IRS" },
            { kind: "ownership", l: "Articles of Organization" },
            { kind: "bank",      l: "Bank verification" },
          ].map(d => {
            const provided = docs.some(x => x.kind === d.kind);
            return (
              <li key={d.kind} className="flex items-center justify-between">
                <span>{d.l}</span>
                {provided
                  ? <span className="chip bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]"><Check className="w-2.5 h-2.5"/>Provided</span>
                  : <span className="chip bg-amber-50 text-amber-700 border border-amber-200 text-[10px]"><AlertCircle className="w-2.5 h-2.5"/>Missing</span>}
              </li>
            );
          })}
        </ul>
      </div>
      <div className="rounded-xl bg-foreground text-background p-5">
        <div className="text-[10px] mono uppercase tracking-widest text-accent/80">What happens next</div>
        <ul className="mt-2 text-sm text-background/80 space-y-1">
          <li>• Middesk runs an automated EIN + entity check (typically &lt; 30s)</li>
          <li>• Plaid (if connected) verifies bank ownership instantly</li>
          <li>• You'll get a notification when status moves to <span className="chip bg-accent/30 border border-accent/40 text-[10px]">Verified</span></li>
        </ul>
      </div>
    </div>
  );
}

/* ─── Shared bits ───────────────────────────────────────────────────────── */
function SidebarItem({ label, muted }) {
  return (
    <div className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm ${muted ? "text-foreground/55" : "text-foreground"}`}>
      <span className="w-4 h-4 rounded-sm bg-foreground/10"/>
      <span>{label}</span>
    </div>
  );
}

function Label({ children }) {
  return <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">{children}</div>;
}

function BuildNotes({ items }) {
  return (
    <div className="mt-8 rounded-xl border border-dashed border-border bg-secondary/20 p-5" data-testid="build-notes">
      <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55 inline-flex items-center gap-1.5">
        <FileText className="w-3 h-3"/>What I'll actually build (after approval)
      </div>
      <ul className="mt-3 space-y-1.5 text-[13px] text-foreground/75">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2"><span className="text-foreground/40 mt-0.5">›</span><span>{it}</span></li>
        ))}
      </ul>
    </div>
  );
}
